/**
 * Der Kunden-Arbeitsbereich /kunden/:id (Redesign 03.09.2026).
 *
 * Kopf: Name, Pakete, Ampeln. Darunter Tabs, die die bestehenden Seiten
 * EINBETTEN — nichts wurde neu gebaut, nur an den Kunden gebunden:
 *   Übersicht · Anfragen · Google Ads · SEO & Blog · formularfrei ·
 *   Monatsbericht · Empfehlungen · Abrechnung · Einstellungen
 * Der einzige Tab, der nichts einbettet, ist formularfrei: Dafür gab es keine
 * bestehende Seite. Das Anfragemodul wird hier verwaltet, weil der Betrieb
 * ohnehin hier ist und keinen zweiten Zugang bekommen soll (Nico, 14.09.2026).
 * Der Tab lebt in der URL (?tab=…), damit ⌘K und Links direkt hinspringen.
 */

import { useMemo } from 'react';
import { Link, useParams, useSearchParams, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ExternalLink, Loader2 } from 'lucide-react';
import { getCustomer, getProjects } from '@/lib/api';
import { getKundenStatus } from '@/lib/analytics-api';
import Ampel from '@/components/Ampel';
import AnalyticsPage, { type AnalyticsTab } from '@/pages/AnalyticsPage';
import LeadsPage from '@/pages/LeadsPage';
import AbrechnungPage from '@/pages/AbrechnungPage';
import FormularfreiPanel from '@/components/analytics/FormularfreiPanel';

type KundenTab = 'uebersicht' | 'anfragen' | 'ads' | 'seo' | 'formularfrei' | 'bericht' | 'empfehlungen' | 'abrechnung' | 'einstellungen';
const TABS: Array<{ key: KundenTab; label: string }> = [
  { key: 'uebersicht', label: 'Übersicht' },
  { key: 'anfragen', label: 'Anfragen' },
  { key: 'ads', label: 'Google Ads' },
  { key: 'seo', label: 'SEO & Blog' },
  { key: 'formularfrei', label: 'formularfrei' },
  { key: 'bericht', label: 'Monatsbericht' },
  { key: 'empfehlungen', label: 'Empfehlungen' },
  { key: 'abrechnung', label: 'Abrechnung' },
  { key: 'einstellungen', label: 'Einstellungen' },
];
/** Unter „SEO & Blog" liegen drei Analytics-Tabs — als zweite Zeile. */
const SEO_UNTER: Array<{ key: AnalyticsTab; label: string }> = [
  { key: 'seo', label: 'Google-Suche' },
  { key: 'blog', label: 'Blog' },
  { key: 'strategie', label: 'Strategie' },
];

export default function KundePage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (TABS.some((t) => t.key === searchParams.get('tab')) ? searchParams.get('tab') : 'uebersicht') as KundenTab;
  const seoUnter = (SEO_UNTER.some((t) => t.key === searchParams.get('seo')) ? searchParams.get('seo') : 'seo') as AnalyticsTab;

  const { data: kunde, isLoading, error } = useQuery({ queryKey: ['customer', id], queryFn: () => getCustomer(id!), enabled: !!id });
  const { data: status } = useQuery({ queryKey: ['kunden-status'], queryFn: getKundenStatus, staleTime: 60_000 });
  const { data: projekte } = useQuery({ queryKey: ['projects', id], queryFn: () => getProjects(id), enabled: !!id && tab === 'einstellungen' });
  const meinStatus = useMemo(() => status?.kunden.find((k) => k.id === id), [status, id]);

  // Tab-Wechsel: nur `tab`/`seo` anfassen, die eingebetteten Seiten verwalten
  // ihre eigenen Parameter (projekt, range, status, …) selbst.
  const setTab = (t: KundenTab) => {
    const next = new URLSearchParams();
    if (t !== 'uebersicht') next.set('tab', t);
    setSearchParams(next, { replace: true });
  };
  const setSeoUnter = (s: AnalyticsTab) => {
    const next = new URLSearchParams(searchParams);
    if (s === 'seo') next.delete('seo'); else next.set('seo', s);
    setSearchParams(next, { replace: true });
  };

  if (!id) return <Navigate to="/kunden" replace />;
  if (isLoading) return <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Lade…</div>;
  if (error || !kunde) return <div className="text-sm text-red-600">Kunde nicht gefunden. <Link to="/kunden" className="underline">Zur Liste</Link></div>;

  return (
    <div className="space-y-4">
      {/* Kopf */}
      <div className="flex flex-wrap items-start gap-3">
        <Link to="/kunden" className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 mt-0.5" title="Zur Kundenliste"><ArrowLeft className="w-4 h-4" /></Link>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-bold text-gray-900 truncate">{kunde.name}</h1>
            {!kunde.active && <span className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">inaktiv</span>}
            {meinStatus?.pakete.map((p) => <span key={p} className="text-[11px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">{p}</span>)}
          </div>
          {meinStatus && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-[13px] text-gray-500">
              {meinStatus.ads.status !== 'keine' && <span className="inline-flex items-center gap-1">Google Ads: <Ampel status={meinStatus.ads.status} text={meinStatus.ads.text} /></span>}
              <span>Kontaktanfragen 30 T.: <b className="text-gray-800">{meinStatus.anfragen_30d.formular + meinStatus.anfragen_30d.klicks}</b></span>
              {meinStatus.bericht.status !== 'keine' && <span className="inline-flex items-center gap-1">Bericht: <Ampel status={meinStatus.bericht.status} text={meinStatus.bericht.text} /></span>}
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 -mx-1 px-1 overflow-x-auto">
        <nav className="flex gap-1 min-w-max">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} className={`px-3 py-2 text-[13px] font-medium border-b-2 -mb-px whitespace-nowrap transition ${tab === t.key ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>{t.label}</button>
          ))}
        </nav>
      </div>

      {/* Inhalt */}
      {tab === 'uebersicht' && <AnalyticsPage embedded={{ customerId: id, tab: 'overview' }} />}
      {tab === 'anfragen' && <LeadsPage fixedCustomerSlug={kunde.slug} />}
      {tab === 'ads' && <AnalyticsPage embedded={{ customerId: id, tab: 'ads' }} />}
      {tab === 'seo' && (
        <div className="space-y-4">
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
            {SEO_UNTER.map((s) => (
              <button key={s.key} onClick={() => setSeoUnter(s.key)} className={`px-3 py-1 text-xs font-medium rounded-md ${seoUnter === s.key ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>{s.label}</button>
            ))}
          </div>
          <AnalyticsPage key={seoUnter} embedded={{ customerId: id, tab: seoUnter }} />
        </div>
      )}
      {tab === 'formularfrei' && <FormularfreiPanel customerId={id} />}
      {tab === 'bericht' && <AnalyticsPage embedded={{ customerId: id, tab: 'bericht' }} />}
      {tab === 'empfehlungen' && <AnalyticsPage embedded={{ customerId: id, tab: 'empfehlungen' }} />}
      {tab === 'abrechnung' && <AbrechnungPage fixedCustomerId={id} />}
      {tab === 'einstellungen' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <section className="bg-white rounded-xl border border-gray-200 p-4">
            <h2 className="text-[13px] font-semibold text-gray-900 mb-3">Kundendaten</h2>
            <dl className="text-[13px] space-y-1.5">
              <div className="flex gap-3"><dt className="w-36 text-gray-500">Name</dt><dd className="text-gray-900">{kunde.name}</dd></div>
              <div className="flex gap-3"><dt className="w-36 text-gray-500">Kürzel</dt><dd className="text-gray-900 font-mono text-xs">{kunde.slug}</dd></div>
              <div className="flex gap-3"><dt className="w-36 text-gray-500">Weiterleitung</dt><dd className="text-gray-900">{kunde.forward_email || '—'}{kunde.forward_cc ? `, CC ${kunde.forward_cc}` : ''}</dd></div>
              <div className="flex gap-3"><dt className="w-36 text-gray-500">Berichts-E-Mail</dt><dd className="text-gray-900">{kunde.report_email || '—'}</dd></div>
              <div className="flex gap-3"><dt className="w-36 text-gray-500">Monatsbericht</dt><dd className="text-gray-900">{kunde.auto_report ? 'automatisch am 1.' : 'manuell'}</dd></div>
              <div className="flex gap-3"><dt className="w-36 text-gray-500">Status</dt><dd className="text-gray-900">{kunde.active ? 'aktiv' : 'inaktiv'}{kunde.software_only ? ' · nur Software' : ''}</dd></div>
            </dl>
            <Link to="/kunden/verwalten" className="inline-flex items-center gap-1 mt-4 text-[13px] text-blue-600 hover:underline">Bearbeiten, einladen, deaktivieren <ExternalLink className="w-3.5 h-3.5" /></Link>
          </section>
          <section className="bg-white rounded-xl border border-gray-200 p-4">
            <h2 className="text-[13px] font-semibold text-gray-900 mb-3">Projekte</h2>
            {!projekte ? <Loader2 className="w-4 h-4 animate-spin text-gray-400" /> : projekte.length === 0 ? <p className="text-[13px] text-gray-400">Noch kein Projekt.</p> : (
              <ul className="divide-y divide-gray-50 text-[13px]">
                {projekte.map((p) => (
                  <li key={p.id} className="py-2 flex items-center gap-2">
                    <span className="flex-1 text-gray-900">{p.name}</span>
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{p.project_type}</span>
                    {p.ads_account_id && <span className="text-[11px] text-gray-400">Ads {p.ads_account_id}</span>}
                    {!p.active && <span className="text-[11px] text-gray-400">inaktiv</span>}
                  </li>
                ))}
              </ul>
            )}
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-4 text-[13px]">
              <Link to={`/projects?customer=${id}`} className="inline-flex items-center gap-1 text-blue-600 hover:underline">Projekte verwalten <ExternalLink className="w-3.5 h-3.5" /></Link>
              <Link to="/einrichtung" className="inline-flex items-center gap-1 text-blue-600 hover:underline">Einrichtung (Tracking, Search Console, Ads) <ExternalLink className="w-3.5 h-3.5" /></Link>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
